import FetchClient, { type ApiResponse } from "./FetchClient";

export interface EmailDailyMetrics {
  date: string;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  complained: number;
}

export interface EmailMetricTotals {
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  complained: number;
}

export interface EmailMetricRates {
  delivery: number;
  open: number;
  click: number;
  bounce: number;
  complaint: number;
}

export interface EmailSourceCount {
  source: string;
  count: number;
}

export interface EmailCampaignSummary {
  campaign: string;
  label: string;
  totals: EmailMetricTotals;
  rates: EmailMetricRates;
}

export interface EmailMonitoringReport {
  campaign: string;
  days: number;
  generatedAt: string;
  trackingSince: string;
  startDate: string;
  endDate: string;
  totals: EmailMetricTotals;
  rates: EmailMetricRates;
  daily: EmailDailyMetrics[];
  campaigns: EmailCampaignSummary[];
  optOuts: {
    current: number;
    withTimestamp: number;
    inRange: number;
    bySource: EmailSourceCount[];
  };
  ses: {
    sendingEnabled: boolean;
    productionAccess: boolean;
    enforcementStatus: string;
    trackingEnabled: boolean;
    max24HourSend: number;
    sentLast24Hours: number;
    remaining24Hours: number;
    maxSendRate: number;
    suppression: {
      total: number;
      byReason: EmailSourceCount[];
    };
  };
}

export async function getEmailMonitoring(
  days = 30,
  campaign = "daily-deals-digest"
): Promise<EmailMonitoringReport> {
  const query = new URLSearchParams({ days: String(days), campaign });
  const response = await FetchClient.get<ApiResponse<EmailMonitoringReport>>(
    `/admin/email-monitoring?${query.toString()}`
  );
  return response.data;
}

export interface EmailProgramGroup {
  key: string;
  sent: number;
  holdout: number;
  delivered: number;
  bounced: number;
  complained: number;
  opened: number;
  clicked: number;
  bouncePct: number;
  complaintPct: number;
  openPct: number;
  clickPct: number;
  unsubscribed: number;
  unsubPct: number;
  treatedAccounts: number;
  treatedConverted: number;
  holdoutAccounts: number;
  holdoutConverted: number;
  maturingAccounts: number;
  treatedRate: number;
  holdoutRate: number;
  absoluteLift: number;
  relativeLift?: number;
}

export interface EmailProgramReturner {
  userId?: string;
  accountKey: string;
  program: string;
  firstCampaign: string;
  lastCampaign: string;
  firstSentAt: number;
  convertedAt: number;
  emails: number;
  clicked: boolean;
  holdout: boolean;
  payingNow: boolean;
  plan?: string;
}

export interface EmailProgramsReport {
  generatedAt: string;
  since: string;
  windowDays: number;
  programs: EmailProgramGroup[];
  campaigns: EmailProgramGroup[];
  returners: EmailProgramReturner[];
}

export async function getEmailPrograms(days = 30, window = 30): Promise<EmailProgramsReport> {
  const query = new URLSearchParams({ days: String(days), window: String(window) });
  const response = await FetchClient.get<ApiResponse<EmailProgramsReport>>(
    `/admin/email-programs?${query.toString()}`
  );
  return response.data;
}
